import React from 'react';
import { NewTable, HStack } from '@carta/ink';
import Ornament, { OrnamentProps } from '../../components/Ornament/Ornament';
import { ornamentVariantColors } from '../../components/_common/Types/GlobalTypes';
import { STATIC_ICONS } from '../../helpers/constants';

import ExposeDescribe from '../components/ExposeDescribe';

const variants: Array<OrnamentProps['variant']> = [
  'feedback-positive',
  'feedback-negative',
  'feedback-notice',
  'feedback-neutral',
  'feedback-pending',
  'feedback-informational',
  'feedback-staff',
];
const sizes: Array<OrnamentProps['size']> = ['small', 'medium', 'large'];

/* Remove file and add samples once Ornament is exported */
const OrnamentExpose = () => {
  return (
    <>
      <ExposeDescribe id="Ornament variants" name="Ornament variants" key="Ornament variants">
        <NewTable>
          <NewTable.Head>
            <NewTable.Row>
              <NewTable.HeadCell width="250px" />
              {sizes.map(size => (
                <NewTable.HeadCell>{size}</NewTable.HeadCell>
              ))}
            </NewTable.Row>
          </NewTable.Head>
          <NewTable.Body>
            {variants.map(variant => (
              <NewTable.Row>
                <NewTable.Cell>
                  <code>{variant}</code>
                </NewTable.Cell>
                {sizes.map(size => (
                  <NewTable.Cell>
                    <Ornament variant={variant} size={size} />
                  </NewTable.Cell>
                ))}
              </NewTable.Row>
            ))}
          </NewTable.Body>
        </NewTable>
      </ExposeDescribe>

      <ExposeDescribe
        id="Ornament with spinning pending variant"
        name="Ornament with spinning pending variant"
        key="Ornament with spinning pending variant"
      >
        <HStack>
          <Ornament variant="feedback-pending" isSpinner size="small" />
          <Ornament variant="feedback-pending" isSpinner size="medium" />
          <Ornament variant="feedback-pending" isSpinner />
        </HStack>
      </ExposeDescribe>

      <ExposeDescribe id="Ornament size small" name="Ornament size small" key="Ornament size small">
        <NewTable>
          <NewTable.Head>
            <NewTable.Row>
              <NewTable.HeadCell width="200px" />
              <NewTable.HeadCell>Regular</NewTable.HeadCell>
              <NewTable.HeadCell>Inverse</NewTable.HeadCell>
              <NewTable.HeadCell>Dashed</NewTable.HeadCell>
            </NewTable.Row>
          </NewTable.Head>
          <NewTable.Body>
            {STATIC_ICONS.map(icon => (
              <NewTable.Row>
                <NewTable.Cell>
                  <code>{icon}</code>
                </NewTable.Cell>
                <NewTable.Cell>
                  <Ornament key={`small-regular-${icon}`} color="orange" size="small" icon={icon} />
                </NewTable.Cell>
                <NewTable.Cell>
                  <Ornament key={`small-inverse-${icon}`} color="orange" size="small" icon={icon} isInverse />
                </NewTable.Cell>
                <NewTable.Cell>
                  <Ornament key={`small-dashed-${icon}`} color="orange" size="small" icon={icon} isDashed />
                </NewTable.Cell>
              </NewTable.Row>
            ))}
          </NewTable.Body>
        </NewTable>
      </ExposeDescribe>

      <ExposeDescribe id="Ornament size medium" name="Ornament size medium" key="Ornament size medium">
        <NewTable>
          <NewTable.Head>
            <NewTable.Row>
              <NewTable.HeadCell width="200px" />
              <NewTable.HeadCell>Regular</NewTable.HeadCell>
              <NewTable.HeadCell>Inverse</NewTable.HeadCell>
              <NewTable.HeadCell>Dashed</NewTable.HeadCell>
            </NewTable.Row>
          </NewTable.Head>
          <NewTable.Body>
            {STATIC_ICONS.map(icon => (
              <NewTable.Row>
                <NewTable.Cell>
                  <code>{icon}</code>
                </NewTable.Cell>
                <NewTable.Cell>
                  <Ornament key={`medium-regular-${icon}`} color="blue" size="medium" icon={icon} />
                </NewTable.Cell>
                <NewTable.Cell>
                  <Ornament key={`medium-inverse-${icon}`} color="blue" size="medium" icon={icon} isInverse />
                </NewTable.Cell>
                <NewTable.Cell>
                  <Ornament key={`medium-dashed-${icon}`} color="blue" size="medium" icon={icon} isDashed />
                </NewTable.Cell>
              </NewTable.Row>
            ))}
          </NewTable.Body>
        </NewTable>
      </ExposeDescribe>

      <ExposeDescribe id="Ornament size large" name="Ornament size large" key="Ornament size large">
        <NewTable>
          <NewTable.Head>
            <NewTable.Row>
              <NewTable.HeadCell width="200px" />
              <NewTable.HeadCell>Regular</NewTable.HeadCell>
              <NewTable.HeadCell>Inverse</NewTable.HeadCell>
              <NewTable.HeadCell>Dashed</NewTable.HeadCell>
            </NewTable.Row>
          </NewTable.Head>
          <NewTable.Body>
            {STATIC_ICONS.map(icon => (
              <NewTable.Row>
                <NewTable.Cell>
                  <code>{icon}</code>
                </NewTable.Cell>
                <NewTable.Cell>
                  <Ornament key={`large-regular-${icon}`} color="red" size="large" icon={icon} />
                </NewTable.Cell>
                <NewTable.Cell>
                  <Ornament key={`large-inverse-${icon}`} color="red" size="large" icon={icon} isInverse />
                </NewTable.Cell>
                <NewTable.Cell>
                  <Ornament key={`large-dashed-${icon}`} color="red" size="large" icon={icon} isDashed />
                </NewTable.Cell>
              </NewTable.Row>
            ))}
          </NewTable.Body>
        </NewTable>
      </ExposeDescribe>

      <ExposeDescribe
        id="Ornament color combinations"
        name="Ornament color combinations"
        key="Ornament color combinations"
      >
        <NewTable>
          <NewTable.Head>
            <NewTable.Row>
              <NewTable.HeadCell width="200px" />
              {ornamentVariantColors.map(color => (
                <NewTable.HeadCell>{color}</NewTable.HeadCell>
              ))}
            </NewTable.Row>
          </NewTable.Head>
          <NewTable.Body>
            {STATIC_ICONS.map(icon => (
              <NewTable.Row>
                <NewTable.Cell>
                  <code>{icon}</code>
                </NewTable.Cell>

                {ornamentVariantColors.map(color => (
                  <NewTable.Cell>
                    <HStack>
                      <Ornament key={`${color}-regular-${icon}`} color={color} icon={icon} />
                      {color !== 'white' && (
                        <>
                          <Ornament key={`${color}-inverse-${icon}`} color={color} icon={icon} isInverse />
                          <Ornament key={`${color}-dashed-${icon}`} color={color} icon={icon} isDashed />
                        </>
                      )}
                    </HStack>
                  </NewTable.Cell>
                ))}
              </NewTable.Row>
            ))}
          </NewTable.Body>
        </NewTable>
      </ExposeDescribe>
    </>
  );
};

export default OrnamentExpose;
